import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { LeadGenResponse } from '../types/api';
import { useLeadGen } from '../hooks/useLeadGen';
import TransferIndicator from './TransferIndicator';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 30px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  font-size: 24px;
  color: #000;
  margin-bottom: 20px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 30px;
  line-height: 1.5;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  border: 2px solid #ddd;
  border-radius: 10px;
  font-size: 18px;
  text-align: center;
  margin-bottom: 20px;
  outline: none;
  
  &:focus {
    border-color: #fff000;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Button = styled.button<{ primary?: boolean }>`
  padding: 15px 30px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin: 0 10px;
  transition: all 0.3s ease;
  
  ${props => props.primary ? `
    background-color: #fff000;
    color: #000;
    
    &:hover {
      background-color: #e6e600;
    }
  ` : `
    background-color: #f5f5f5;
    color: #666;
    
    &:hover {
      background-color: #e8e8e8;
    }
  `}
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  font-size: 14px;
  margin-top: 10px;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #000;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  display: inline-block;
  margin-right: 10px;
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 14px;
  margin-top: 10px;
`;

interface LeadGenValidationProps {
  hash: string;
  onValidationSuccess: (data: LeadGenResponse) => void;
  onValidationError: () => void;
}

const LeadGenValidation: React.FC<LeadGenValidationProps> = ({
  hash,
  onValidationSuccess,
  onValidationError
}) => {
  const [dniLastDigits, setDniLastDigits] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [userName, setUserName] = useState('');
  const [showNewFlowButton, setShowNewFlowButton] = useState(false);
  const [errorType, setErrorType] = useState<'retry' | 'newFlow' | 'autoRedirect'>('retry');
  const [validatedData, setValidatedData] = useState<any>(null); // Guardar datos localmente
  const navigate = useNavigate();
  const { validateLeadGen } = useLeadGen();

  const maxAttempts = 3;

  const handleValidation = async () => {
    if (dniLastDigits.length !== 3) {
      setError('Por favor ingresa los últimos 3 dígitos de tu DNI');
      return;
    }

    setLoading(true);
    setError('');
    setShowNewFlowButton(false);

    try {
      const data = await validateLeadGen(hash, dniLastDigits);
      
      // Guardar los datos validados localmente
      setValidatedData(data);
      
      // Extraer el nombre del usuario para el mensaje de bienvenida
      const personalInfo = data.data.personalInfo;
      if (personalInfo) {
        const firstName = personalInfo.firstName.split(' ')[0]; // Solo el primer nombre
        setUserName(firstName);
      }
      
      // Marcar como validación exitosa
      setValidationSuccess(true);
    } catch (error: any) {
      console.error('Error validating lead gen:', error);
      
      const shouldAllowRetry = error.shouldAllowRetry;
      const shouldShowNewFlowButton = error.shouldShowNewFlowButton;
      const shouldAutoRedirect = error.shouldAutoRedirect;
      
      if (shouldAllowRetry) {
        // Errores que permiten reintento (VALIDATION_ERROR, DNI_VALIDATION_FAILED)
        setAttempts(prev => prev + 1);
        setDniLastDigits('');
        setErrorType('retry');
        
        if (attempts + 1 >= maxAttempts) {
          setError('Máximo de intentos alcanzado. Serás redirigido al inicio.');
          setTimeout(() => {
            onValidationError();
          }, 3000);
        } else {
          setError(`${error.message}. Te quedan ${maxAttempts - attempts - 1} intentos.`);
        }
      } else if (shouldShowNewFlowButton) {
        // Errores que requieren nuevo flujo
        setErrorType('newFlow');
        setShowNewFlowButton(true);
        
        // Mensajes especiales para casos específicos
        if (error.errorCode === 'REQUEST_ALREADY_REDEEMED') {
          setError('Esta oferta ya fue utilizada. ¿Quieres ver nuevas ofertas disponibles?');
        } else if (error.errorCode === 'REQUEST_EXPIRED') {
          setError('Esta oferta expiró. Te mostraremos las ofertas actuales disponibles.');
        } else {
          setError(error.message);
        }
      } else if (shouldAutoRedirect) {
        // Errores que redirigen automáticamente
        setErrorType('autoRedirect');
        setError(error.message);
        setTimeout(() => {
          onValidationError();
        }, 3000);
      } else {
        // Fallback para errores no manejados
        setError('Error inesperado. Serás redirigido al inicio.');
        setTimeout(() => {
          onValidationError();
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    console.log('LeadGenValidation - handleContinue llamado con validatedData:', validatedData);
    // Llamar a onValidationSuccess con los datos validados guardados localmente
    onValidationSuccess(validatedData);
  };

  const handleNewFlow = () => {
    onValidationError();
  };

  // Si la validación fue exitosa, mostrar mensaje de bienvenida
  if (validationSuccess) {
    return (
      <ModalOverlay>
        <ModalContent>
          <TransferIndicator currentStep="prestix" />
          
          <Title>¡Bienvenido/a {userName}!</Title>
          <Subtitle>
            Continuaremos con tu solicitud de préstamo
          </Subtitle>
          
          <div style={{ marginTop: '20px' }}>
            <Button 
              primary 
              onClick={handleContinue}
            >
              Continuar
            </Button>
          </div>
        </ModalContent>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay>
      <ModalContent>
        <TransferIndicator currentStep="prestix" />
        
        <Title>Validación de Identidad</Title>
        <Subtitle>
          Para continuar con tu solicitud de préstamo, 
          confirma los últimos 3 dígitos de tu DNI.
        </Subtitle>
        
        <Input
          type="text"
          placeholder="Últimos 3 dígitos"
          value={dniLastDigits}
          onChange={(e) => setDniLastDigits(e.target.value.replace(/\D/g, '').slice(0, 3))}
          maxLength={3}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && dniLastDigits.length === 3) {
              handleValidation();
            }
          }}
          disabled={loading || showNewFlowButton}
        />
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <div style={{ marginTop: '20px' }}>
          {!showNewFlowButton ? (
            <Button 
              primary 
              onClick={handleValidation} 
              disabled={dniLastDigits.length !== 3 || loading}
            >
              {loading && <LoadingSpinner />}
              {loading ? 'Validando...' : 'Verificar'}
            </Button>
          ) : (
            <Button 
              primary 
              onClick={handleNewFlow}
            >
              Empezar nueva solicitud
            </Button>
          )}
          
          {errorType === 'retry' && attempts >= maxAttempts && (
            <Button onClick={handleNewFlow} disabled={loading}>
              Empezar de nuevo
            </Button>
          )}
        </div>
      </ModalContent>
    </ModalOverlay>
  );
};

export default LeadGenValidation;
